import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useCapability, useComponentProps, useComponentState, useInvokeFunction, useToggleComponent } from 'navigation-components/platform/hooks';
import { GLOBAL_SEARCH_COMPONENT, globalSearchComponentDef } from 'navigation-components/config';
import { capabilities } from 'navigation-components/capabilities';
import useKeyboardShortcutsGlobal from '../hooks/useKeyboardShortcutsGlobal';
import useKeyboardShortcutsSearch from '../hooks/useKeyboardShortcutsSearch';
import useSearchComponent from '../hooks/useSearchComponent';
import { SEARCH_TRACKING_CONSTANTS, handleError, GLOBAL_SEARCH_FULL_PAGE_SEARCH_QUERY_PARAM } from '../constants';
import { devShouldKeepMinifiedOpen } from '../utils/development';
import { ClearSearchIcon } from './ClearSearchIcon';
import { warmupSearchCache } from '../utils/warmupSearchCache';
import { getFullSearchUrl } from 'global-search-shared/utils/getFullSearchUrl';
import { BREAKPOINTS } from 'global-search-shared';
import { getMinSearchQueryLen } from 'global-search-shared/utils/getMinSearchQueryLen';
import { navigateTo } from 'global-search-shared/utils/navigateTo';
import { useMediaQuery } from 'global-search-shared/hooks/useMediaQuery';
import useHandlePreFilteredSearch from '../hooks/useHandlePreFilteredSearch';
import { CloseSearchButton } from './CloseButton';
import { StyledClearSearchButton, StyledIframeWrapper, StyledKey, StyledSearchContainer, StyledSearchControls, StyledSearchForm, StyledSearchIcon, StyledSearchInput, StyledSearchScrollableWrapper, StyledSearchWrapper } from './styled';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
function getFullPageSearchQueryParam() {
  const urlQueryParams = new URLSearchParams(window.location.search);
  return urlQueryParams.get(GLOBAL_SEARCH_FULL_PAGE_SEARCH_QUERY_PARAM) || '';
}
export const SearchInput = ({
  isMac,
  trackSearchEvent,
  translatedPlaceholderText,
  translatedPlaceholderTextShort,
  translatedClearSearchLabel,
  translatedCloseSearchLabel = 'Close',
  hasBreezeWidget = false,
  hasUnifiedSearchApp = false
}) => {
  const screenSize = useMediaQuery(BREAKPOINTS, 'xlarge');
  const {
    subscribe: subscribeSearchChanged
  } = useCapability(capabilities.CHANGE_SEARCH_CAP.getName());
  const {
    subscribe: subscribeFilterChanged
  } = useCapability(capabilities.CHANGE_SEARCH_FILTER_CAP.getName());
  const {
    loadAndShowSearch,
    updateSearchComponentProps
  } = useSearchComponent(true);
  const {
    onToggle,
    toggle
  } = useToggleComponent(GLOBAL_SEARCH_COMPONENT);
  const {
    isOpen
  } = useComponentState(GLOBAL_SEARCH_COMPONENT);
  const searchComponentProps = useComponentProps(globalSearchComponentDef);
  const {
    invoke
  } = useInvokeFunction(globalSearchComponentDef);
  const searchForm = useRef(null);
  const searchInput = useRef(null);
  const searchContainerRef = useRef(null);
  const iframeWrapperRef = useRef(null);
  const wrapperRef = useRef(null);
  const focusSearch = () => {
    if (searchInput && searchInput.current) {
      searchInput.current.focus();
    }
  };
  const onOpen = useCallback(() => {
    void warmupSearchCache();
    focusSearch();
  }, []);
  const [canScroll, setCanScroll] = useState(true);
  useEffect(() => {
    var _iframeWrapperRef$cur;
    if (!isOpen) return;
    const iframe = (_iframeWrapperRef$cur = iframeWrapperRef.current) === null || _iframeWrapperRef$cur === void 0 ? void 0 : _iframeWrapperRef$cur.querySelector('iframe');
    if (!iframe) return;
    const onStart = () => {
      setCanScroll(false);
    };
    const onEnd = () => {
      setCanScroll(true);
    };
    iframe.addEventListener('transitionstart', onStart);
    iframe.addEventListener('transitionend', onEnd);
    return () => {
      iframe.removeEventListener('transitionstart', onStart);
      iframe.removeEventListener('transitionend', onEnd);
    };
  }, [isOpen]);
  const handleSearchChanged = useCallback(({
    query,
    filters
  }) => {
    loadAndShowSearch({
      searchQuery: query === undefined ? searchComponentProps().searchQuery : query,
      searchFilters: filters === undefined ? searchComponentProps().searchFilters : filters.length ? filters.join(',') : '',
      searchPreFiltered: filters === undefined ? searchComponentProps().searchPreFiltered : ''
    });
  }, [loadAndShowSearch, searchComponentProps]);
  const handleFilterChanged = useCallback(({
    filters
  }) => {
    loadAndShowSearch({
      searchQuery: searchComponentProps().searchQuery,
      searchFilters: filters.length ? filters.join(',') : ''
    });
  }, [loadAndShowSearch, searchComponentProps]);
  const handleShouldClose = useCallback(() => {
    // The activeElement is initially the body and then changes to the input,
    // even when you tab directly into the input so this caters for that
    window.setTimeout(() => {
      var _searchForm$current;
      if (!(searchForm !== null && searchForm !== void 0 && (_searchForm$current = searchForm.current) !== null && _searchForm$current !== void 0 && _searchForm$current.contains(document.activeElement)) && !devShouldKeepMinifiedOpen()) {
        toggle(false);
      }
    });
  }, [toggle]);

  // This handles where users are in the search input with search open, and
  // then Shift + Tab backwards out of the input.The focus handler on the
  // window doesn't catch this as the main window already has focus
  const handleBlurForm = useCallback(() => {
    // The activeElement is initially the body and then changes to the input,
    // even when you tab directly into the input so this caters for that
    window.setTimeout(() => {
      var _document$activeEleme, _document$activeEleme2;
      if (((_document$activeEleme = document.activeElement) === null || _document$activeEleme === void 0 ? void 0 : _document$activeEleme.id) !== 'clear-search-btn' && ((_document$activeEleme2 = document.activeElement) === null || _document$activeEleme2 === void 0 ? void 0 : _document$activeEleme2.id) !== 'global-search') {
        handleShouldClose();
      }
    });
  }, [handleShouldClose]);
  useEffect(() => onToggle(isCurrentlyOpen => {
    (isCurrentlyOpen ? onOpen : function onClose() {})();
  }), [onOpen, onToggle]);
  useEffect(() => {
    return subscribeSearchChanged(handleSearchChanged);
  }, [handleSearchChanged, subscribeSearchChanged]);
  useEffect(() => {
    return subscribeFilterChanged(handleFilterChanged);
  }, [handleFilterChanged, subscribeFilterChanged]);
  useEffect(() => {
    // on initial load, populate the searchComponentProps with the
    // query/filters/preFiltered from the querystring
    const fullPageSearchQuery = getFullPageSearchQueryParam();
    if (fullPageSearchQuery) {
      updateSearchComponentProps({
        searchQuery: fullPageSearchQuery
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Close search if you click away from it. The iframe is a different window,
  // so clicks there don't register here and so won't close search. The click
  // handler is necessary if the user is in the
  useEffect(() => {
    if (isOpen) {
      window.addEventListener('click', handleShouldClose);
      window.addEventListener('focus', handleShouldClose);
    }
    return () => {
      if (isOpen) {
        window.removeEventListener('click', handleShouldClose);
        window.removeEventListener('focus', handleShouldClose);
      }
    };
  }, [handleBlurForm, handleShouldClose, isOpen]);
  const onClick = useCallback(e => {
    e.preventDefault();
    if (!isOpen) {
      loadAndShowSearch();
      trackSearchEvent({
        type: SEARCH_TRACKING_CONSTANTS.OPEN,
        value: SEARCH_TRACKING_CONSTANTS.MOUSE
      });
    }
  }, [isOpen, loadAndShowSearch, trackSearchEvent]);
  const handleSearchInputFocus = useCallback(() => {
    invoke('searchInFocus', {
      isFocused: true
    }).catch(handleError);
  }, [invoke]);
  const handleSearchInputBlur = useCallback(() => {
    invoke('searchInFocus', {
      isFocused: false
    }).catch(handleError);
  }, [invoke]);
  const onChange = useCallback(e => {
    if (e.target) {
      // This ensures the search will work of the user right-clicks and pastes
      // a query into the search box

      loadAndShowSearch({
        searchQuery: e.target.value,
        searchFilters: searchComponentProps().searchFilters,
        searchPreFiltered: searchComponentProps().searchPreFiltered
      });
    }
  }, [loadAndShowSearch, searchComponentProps]);
  useKeyboardShortcutsGlobal({
    isMac,
    trackSearchEvent
  });
  useKeyboardShortcutsSearch({
    isMac,
    trackSearchEvent,
    searchInput
  });
  useHandlePreFilteredSearch();
  const {
    searchQuery
  } = searchComponentProps();
  const handleKeyDown = useCallback(event => {
    const current = searchInput.current;
    if (event.key === 'Enter') {
      // Handle breeze AI shift + enter keyboard shortcut
      if (hasBreezeWidget && event.shiftKey) {
        event.stopPropagation();
        event.preventDefault();
        invoke('executeBreezeWidget', {}).catch(handleError);
        return false;
      }
      if (hasUnifiedSearchApp) {
        loadAndShowSearch({
          searchQuery: searchComponentProps().searchQuery,
          searchFilters: searchComponentProps().searchFilters,
          searchPreFiltered: searchComponentProps().searchPreFiltered
        });
        return false;
      }
      if (current && current.value.trim().length >= getMinSearchQueryLen(searchComponentProps().searchQuery)) {
        trackSearchEvent({
          type: SEARCH_TRACKING_CONSTANTS.ENTER,
          value: current.value.length.toString()
        });
        navigateTo(getFullSearchUrl(searchQuery));
      }
    }
  }, [trackSearchEvent, searchQuery, searchComponentProps, loadAndShowSearch, hasBreezeWidget, hasUnifiedSearchApp, invoke]);
  useEffect(() => {
    if (!searchInput.current) return;
    const inputRef = searchInput.current;
    inputRef.addEventListener('keydown', handleKeyDown);
    return () => {
      inputRef.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);
  const onFormSubmit = e => {
    e.preventDefault();
    if (!isOpen) {
      loadAndShowSearch({
        searchQuery: searchComponentProps().searchQuery,
        searchFilters: searchComponentProps().searchFilters,
        searchPreFiltered: searchComponentProps().searchPreFiltered
      });
    }
  };
  const handleClearButtonClick = useCallback(e => {
    var _searchInput$current;
    updateSearchComponentProps({
      searchQuery: ''
    });
    searchInput === null || searchInput === void 0 || (_searchInput$current = searchInput.current) === null || _searchInput$current === void 0 || _searchInput$current.focus();
    if (!isOpen) {
      toggle(true);
    }
    e.preventDefault();
    e.stopPropagation();
  }, [isOpen, toggle, updateSearchComponentProps]);
  const handleCloseButtonClick = useCallback(e => {
    toggle(false);
    e.preventDefault();
    e.stopPropagation();
  }, [toggle]);
  const setWrapperSize = useCallback(({
    height,
    width
  }) => {
    if (wrapperRef.current) {
      invoke('searchWrapperResize', {
        height,
        width
      }).catch(handleError);
    }
  }, [invoke]);
  useLayoutEffect(() => {
    // Disable iframe resizing in tests
    if (typeof jasmine !== 'undefined') return;
    if (wrapperRef.current) {
      const contentRect = wrapperRef.current.getBoundingClientRect();
      setWrapperSize({
        height: contentRect.height,
        width: contentRect.width
      });
      const observer = new ResizeObserver(entries => {
        for (const entry of entries) {
          const height = Math.ceil(entry.contentRect.height);
          const width = Math.ceil(entry.contentRect.width);
          setWrapperSize({
            height,
            width
          });
        }
      });
      observer.observe(wrapperRef.current, {
        box: 'border-box'
      });
      return () => {
        observer.disconnect();
      };
    }
  }, [setWrapperSize]);
  return /*#__PURE__*/_jsx(StyledSearchContainer, {
    ref: searchContainerRef,
    children: /*#__PURE__*/_jsx(StyledSearchWrapper, {
      isOpen: isOpen,
      children: /*#__PURE__*/_jsxs(StyledSearchScrollableWrapper, {
        isOpen: isOpen,
        disableScroll: !canScroll,
        ref: wrapperRef,
        children: [/*#__PURE__*/_jsxs(StyledSearchForm, {
          id: "hs-search-react",
          ref: searchForm,
          onSubmit: onFormSubmit,
          onBlur: handleBlurForm,
          isOpen: isOpen,
          children: [/*#__PURE__*/_jsx(StyledSearchIcon, {
            version: "1.1",
            xmlns: "http://www.w3.org/2000/svg",
            xmlnsXlink: "http://www.w3.org/1999/xlink",
            x: "0",
            y: "0",
            viewBox: "0 0 31 31",
            xmlSpace: "preserve",
            width: "14px",
            display: "flex",
            children: /*#__PURE__*/_jsx("g", {
              children: /*#__PURE__*/_jsx("g", {
                transform: "translate(-116.000000, -3779.000000)",
                children: /*#__PURE__*/_jsx("path", {
                  d: "M127.7,3799.4c-4.8,0-8.7-3.9-8.7-8.7c0-4.8,3.9-8.7,8.7-8.7c4.8,0,8.7,3.9,8.7,8.7 C136.4,3795.5,132.5,3799.4,127.7,3799.4 M146.1,3806.9l-9.1-9.1c1.5-2,2.4-4.4,2.4-7.1c0-6.4-5.2-11.7-11.7-11.7 c-6.4,0-11.7,5.2-11.7,11.7c0,6.4,5.2,11.7,11.7,11.7c2.7,0,5.2-0.9,7.1-2.4l9.1,9.1c0.3,0.3,0.7,0.4,1.1,0.4 c0.4,0,0.8-0.1,1.1-0.4C146.6,3808.5,146.6,3807.5,146.1,3806.9"
                })
              })
            })
          }), /*#__PURE__*/_jsx(StyledSearchInput, {
            id: "global-search-input",
            name: "global-search-input",
            type: "search",
            "data-test-id": "global-search-input-react",
            "aria-label": translatedPlaceholderText,
            autoComplete: "off",
            minLength: 1,
            maxLength: 512,
            placeholder: screenSize === 'small' && translatedPlaceholderTextShort ? translatedPlaceholderTextShort : translatedPlaceholderText,
            value: searchQuery,
            onChange: onChange,
            onClick: onClick,
            ref: searchInput,
            onFocus: handleSearchInputFocus,
            onBlur: handleSearchInputBlur,
            isOpen: isOpen,
            hasValue: !!searchQuery
          }), /*#__PURE__*/_jsxs(StyledSearchControls, {
            isOpen: isOpen,
            hasValue: !!searchQuery,
            children: [searchQuery ? /*#__PURE__*/_jsx(StyledClearSearchButton, {
              type: "button",
              id: "clear-search-btn",
              "data-test-id": "minified-search-clear-btn",
              "aria-label": translatedClearSearchLabel,
              onClick: handleClearButtonClick,
              children: /*#__PURE__*/_jsx(ClearSearchIcon, {})
            }) : !isOpen && screenSize !== 'small' && /*#__PURE__*/_jsx(_Fragment, {
              children: isMac ? /*#__PURE__*/_jsxs(_Fragment, {
                children: [/*#__PURE__*/_jsx(StyledKey, {
                  isOpen: isOpen,
                  children: "\u2318"
                }), /*#__PURE__*/_jsx(StyledKey, {
                  isOpen: isOpen,
                  children: "K"
                })]
              }) : /*#__PURE__*/_jsxs(_Fragment, {
                children: [/*#__PURE__*/_jsx(StyledKey, {
                  isOpen: isOpen,
                  children: "Ctrl"
                }), /*#__PURE__*/_jsx(StyledKey, {
                  isOpen: isOpen,
                  children: "K"
                })]
              })
            }), isOpen && !searchQuery && ['small', 'medium'].includes(screenSize) && /*#__PURE__*/_jsx(CloseSearchButton, {
              onClose: handleCloseButtonClick,
              children: translatedCloseSearchLabel
            })]
          })]
        }), /*#__PURE__*/_jsx(StyledIframeWrapper, {
          id: "search-results",
          ref: iframeWrapperRef
        })]
      })
    })
  });
};